<template>
  <div class="product-specs-accordion">
    <div
      v-for="(item, idx) in items"
      :key="idx"
      class="spec-product"
      :class="{ 'spec-product--open': isOpen(idx) }"
    >
      <button type="button" class="spec-product__header" @click="toggle(idx)">
        <span class="spec-product__name">{{ item.name }}</span>
        <span
          v-if="item.badge"
          class="spec-product__badge"
          :class="`spec-product__badge--${item.badgeVariant || 'default'}`"
        >
          {{ item.badge }}
        </span>
        <span v-if="item.quantity > 1" class="spec-product__qty">{{ item.quantity }}x</span>
        <span v-if="item.price != null" class="spec-product__price">
          {{ formatPrice(item.price) }}
        </span>
        <span class="spec-product__count" v-if="item.specs && item.specs.length > 0">
          {{ item.specs.length }} specificaties
        </span>
        <BaseIcon name="chevron" size="14" class="spec-product__chevron" />
      </button>
      <dl
        v-if="item.specs && item.specs.length > 0"
        v-show="isOpen(idx)"
        class="spec-product__content"
      >
        <div v-for="(spec, sidx) in item.specs" :key="sidx" class="spec-product__row">
          <dt
            class="spec-product__label"
            :class="{ 'spec-product__label--flagged': isFlagged(spec.label) }"
          >
            {{ spec.label }}
            <span
              v-if="isFlagged(spec.label)"
              class="spec-product__flag"
              :title="flaggedTitle"
            >
              <BaseIcon name="information-circle" size="12" />
            </span>
          </dt>
          <dd class="spec-product__value">{{ spec.value }}</dd>
        </div>
      </dl>
      <p
        v-else-if="item.emptyNote"
        v-show="isOpen(idx)"
        class="spec-product__note"
      >
        {{ item.emptyNote }}
      </p>
    </div>

    <div v-if="items.length === 0" class="product-specs-accordion__empty">
      {{ emptyText }}
    </div>
  </div>
</template>

<script>
  import { defineComponent, reactive, watch } from 'vue';
  import BaseIcon from '@shared/components/BaseIcon.vue';

  export default defineComponent({
    name: 'ProductSpecsAccordion',

    components: { BaseIcon },

    props: {
      items: {
        type: Array,
        required: true,
        validator: (val) => val.every((i) => typeof i.name === 'string'),
      },
      emptyText: {
        type: String,
        default: 'Geen specificaties beschikbaar.',
      },
      defaultOpen: {
        type: Boolean,
        default: true,
      },
      flaggedLabels: {
        type: Array,
        default: () => [],
      },
      flaggedTitle: {
        type: String,
        default: '',
      },
    },

    setup(props) {
      const openState = reactive({});

      const isOpen = (idx) => openState[idx] ?? (props.defaultOpen && idx === 0);

      const toggle = (idx) => {
        openState[idx] = !isOpen(idx);
      };

      const isFlagged = (label) => props.flaggedLabels.includes(label);

      watch(
        () => props.items,
        () => {
          Object.keys(openState).forEach((k) => delete openState[k]);
        }
      );

      const formatPrice = (value) => {
        if (value == null) return '';
        return new Intl.NumberFormat('nl-NL', {
          style: 'currency',
          currency: 'EUR',
          minimumFractionDigits: 2,
        }).format(value);
      };

      return { openState, isOpen, toggle, isFlagged, formatPrice };
    },
  });
</script>

<style lang="scss" scoped>
  .product-specs-accordion {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
    font-size: 0.875rem;

    &__empty {
      color: var(--slate-500);
      font-style: italic;
    }
  }

  .spec-product {
    border: 1px solid var(--slate-200);
    border-radius: 0.5rem;
    overflow: hidden;

    &__header {
      width: 100%;
      display: flex;
      align-items: center;
      gap: 0.5rem;
      padding: 0.625rem 0.75rem;
      background: var(--slate-50);
      border: none;
      cursor: pointer;
      font-size: 0.875rem;
      text-align: left;
      transition: background 0.15s ease;

      &:hover {
        background: var(--slate-100);
      }
    }

    &__name {
      flex: 1;
      font-weight: 600;
      color: var(--slate-800);
    }

    &__badge {
      flex-shrink: 0;
      font-size: 0.6875rem;
      font-weight: 600;
      padding: 0.125rem 0.4375rem;
      border-radius: 0.25rem;
      background: var(--slate-100);
      color: var(--slate-600);

      &--catalog {
        background: var(--brand-100);
        color: var(--brand-800);
      }
    }

    &__qty {
      font-size: 0.75rem;
      font-weight: 500;
      color: var(--slate-600);
      background: var(--slate-200);
      padding: 0.125rem 0.375rem;
      border-radius: 0.25rem;
    }

    &__price {
      font-weight: 500;
      color: var(--slate-700);
    }

    &__count {
      font-size: 0.75rem;
      color: var(--slate-500);
    }

    &__chevron {
      flex-shrink: 0;
      transition: transform 0.2s ease;
      color: var(--slate-400);
      transform: rotate(180deg);
    }

    &--open &__chevron {
      transform: rotate(0deg);
    }

    &--open &__header {
      background: var(--white);
      border-bottom: 1px solid var(--slate-100);
    }

    &__content {
      margin: 0;
      padding: 0.75rem;
      display: grid;
      grid-template-columns: 1fr auto;
      gap: 0.25rem 1rem;
      font-size: 0.8125rem;
      background: var(--white);
    }

    &__row {
      display: contents;
    }

    &__label {
      margin: 0;
      color: var(--slate-500);
      font-weight: 400;

      &--flagged {
        display: inline-flex;
        align-items: center;
        gap: 0.25rem;
      }
    }

    &__flag {
      display: inline-flex;
      color: var(--slate-400);
      cursor: help;
    }

    &__value {
      margin: 0;
      color: var(--slate-800);
      font-weight: 500;
      overflow-wrap: anywhere;
    }

    @include sm-down {
      &__content {
        display: flex;
        flex-direction: column;
        gap: 0.5rem;
      }

      &__row {
        display: flex;
        flex-direction: column;
        gap: 0.125rem;
      }
    }

    &__note {
      margin: 0;
      padding: 0.75rem;
      font-size: 0.8125rem;
      color: var(--slate-500);
      font-style: italic;
      background: var(--white);
    }
  }
</style>
